import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import { useTheme } from '../hooks/useTheme';

export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  useTheme();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // try/finally: login() бросает при сетевой ошибке или не-JSON ответе (502 nginx
    // во время деплоя) — раньше спиннер крутился вечно, а ошибка не показывалась.
    try {
      const result = await authService.login(username, password);
      if (!result.success) {
        setError(result.error || 'Ошибка входа');
        return;
      }
      // Обычный пользователь логинился успешно, RequireAuth молча возвращал его
      // на /login — выглядело как «кнопка не работает». Говорим прямо и не
      // оставляем его токены в localStorage.
      if (!authService.isAdmin()) {
        await authService.logout();
        setError('У этой учётной записи нет прав администратора');
        return;
      }
      navigate('/admin');
    } catch {
      setError('Сервер недоступен, попробуйте позже');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-slate-900">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,var(--color-slate-900),var(--color-slate-950),black)] z-0" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-[100px] animate-pulse" />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-[100px] animate-pulse"
        style={{ animationDelay: '1s' }}
      />

      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 p-10 rounded-3xl w-full max-w-md shadow-2xl relative z-10">
        <div className="text-center mb-10">
          <img src="/favicon/favicon.svg" className="w-16 h-16 object-contain mb-6 mx-auto" alt="Wite Lab" />
          <h1 className="text-4xl font-bold text-white tracking-tight">Wite Lab</h1>
          <p className="text-slate-400 mt-3 font-medium">Панель управления</p>
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-300 p-4 rounded-xl mb-6 text-sm text-center border border-red-500/20 flex items-center justify-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
              Логин
            </label>
            <input
              type="text"
              className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-5 py-4 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all placeholder-slate-600"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(''); }}
              placeholder="admin"
              autoFocus
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
              Пароль
            </label>
            <input
              type="password"
              className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-5 py-4 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all placeholder-slate-600"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Введите пароль"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full py-4 text-lg bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-colors shadow-xl shadow-indigo-900/20 mt-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-slate-400 border-t-slate-900 rounded-full animate-spin" />
            ) : (
              <>
                Войти
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500">Restricted Access System</p>
        </div>
      </div>
    </div>
  );
}
